import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState } from '@/tools/tool.utilities';
import { renameFormulaVariables } from '@/utilities/formulaVariables.utilities';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { FormulaParameter, FormulaToolParameterItem } from '@/tools/formula/formulaTool.types';
import type { BaseToolPersistedState, ToolRehydratePayload } from '@/tools/tool.types';
import type { FormulaDocSummaryOutputV1 } from '@seeqdev/api/model/FormulaDocSummaryOutputV1';

export interface FormulaToolState extends Omit<BaseToolPersistedState, 'originalParameters'> {
  /** Formula text last committed from the editor. The editor keeps in-progress edits locally. */
  formula: string;
  /** Formula variables, each binding a `$identifier` to an item. */
  parameters: FormulaParameter[];
  /** Every item bound to a variable so far, so the variable item selectors keep listing them. */
  originalParameters: FormulaToolParameterItem[];
  /** Formula documentation pages visited, most recent last. */
  navigationStack: string[];
  /** Documentation search requested by the editor's context help; the documentation panel consumes and clears it. */
  formulaFilter: string | undefined;
  /** Formula function summaries used for autocomplete and help, or `undefined` if the fetch returned none. */
  operators: FormulaDocSummaryOutputV1[] | undefined;
}

export type FormulaToolDehydratedState = Omit<FormulaToolState, 'operators' | 'navigationStack'>;

export interface FormulaToolActions {
  /** Sets the name of the formula item being created or edited. */
  setSearchName: (name: string) => void;
  /** Replaces the formula text. */
  setFormula: (formula: string) => void;
  /** Adds a variable and remembers its item in `originalParameters`. */
  addParameter: (parameter: FormulaParameter & { name?: string }) => void;
  /** Replaces all variables. Unlike `addParameter`, does not add their items to `originalParameters`. */
  setParameters: (parameters: FormulaParameter[]) => void;
  /**
   * Replaces the variable at `index` and remembers its item in `originalParameters`. If the identifier changed, the
   * references to the old identifier in the formula are renamed.
   */
  updateParameter: (index: number, parameter: FormulaParameter) => void;
  /**
   * Removes the variable with the given identifier; does nothing if there is none. The formula and
   * `originalParameters` are left unchanged.
   */
  removeParameter: (identifier: string) => void;
  /** Removes every variable. The formula and `originalParameters` are left unchanged. */
  removeAllParameters: () => void;
  /** Replaces the documentation pages visited, most recent last. */
  setNavigationStack: (navigationStack: string[]) => void;
  /** Opens a documentation page on top of the pages already visited. */
  appendToNavigationStack: (step: string) => void;
  /** Requests a documentation search for the documentation panel to run, or `undefined` once it has been consumed. */
  setFormulaFilter: (formulaFilter: string | undefined) => void;
  /** Sets the formula function summaries used for autocomplete and help, or `undefined` if the fetch returned none. */
  setOperators: (operators: FormulaDocSummaryOutputV1[] | undefined) => void;
  /** Loads a saved formula item, or one being copied to Formula, into the tool. */
  rehydrateForEdit: (payload: ToolRehydratePayload) => void;
  /** Resets the store to its initial state, applying any supplied overrides on top of it. */
  reset: ResettableStoreState<FormulaToolState>;
}

const initialState: FormulaToolState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  formula: '',
  parameters: [],
  navigationStack: [],
  formulaFilter: undefined,
  operators: [],
});

/**
 * Picks the state saved in worksteps, leaving out the session-only documentation state.
 */
export const dehydrateFormulaToolState = (state: FormulaToolState): FormulaToolDehydratedState => ({
  id: state.id,
  name: state.name,
  originalParameters: state.originalParameters,
  maximumDuration: state.maximumDuration,
  advancedParametersCollapsed: state.advancedParametersCollapsed,
  configVersion: state.configVersion,
  formula: state.formula,
  parameters: state.parameters,
  formulaFilter: state.formulaFilter,
});

function addItemToOriginalParameters(draft: FormulaToolState, item: FormulaToolParameterItem) {
  if (!draft.originalParameters.some((originalParameter) => originalParameter.id === item.id)) {
    draft.originalParameters.push(item);
  }
}

const formulaToolActionsFactory: ActionsFactory<FormulaToolState, FormulaToolActions> = (set, _get, resetState) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setFormula: (formula) =>
    set((draft) => {
      draft.formula = formula;
    }),

  addParameter: (parameter) =>
    set((draft) => {
      draft.parameters.push(parameter);
      addItemToOriginalParameters(draft, parameter.item);
    }),

  setParameters: (parameters) =>
    set((draft) => {
      draft.parameters = parameters;
    }),

  updateParameter: (index, parameter) =>
    set((draft) => {
      const oldIdentifier = draft.parameters[index]?.identifier;
      draft.parameters[index] = parameter;
      addItemToOriginalParameters(draft, parameter.item);
      if (oldIdentifier && oldIdentifier !== parameter.identifier) {
        draft.formula = renameFormulaVariables(draft.formula, new Map([[oldIdentifier, parameter.identifier]]));
      }
    }),

  removeParameter: (identifier) =>
    set((draft) => {
      const index = draft.parameters.findIndex((parameter) => parameter.identifier === identifier);
      if (index >= 0) {
        draft.parameters.splice(index, 1);
      }
    }),

  removeAllParameters: () =>
    set((draft) => {
      draft.parameters = [];
    }),

  setNavigationStack: (navigationStack) =>
    set((draft) => {
      draft.navigationStack = navigationStack;
    }),

  appendToNavigationStack: (step) =>
    set((draft) => {
      draft.navigationStack.push(step);
    }),

  setFormulaFilter: (formulaFilter) =>
    set((draft) => {
      draft.formulaFilter = formulaFilter;
    }),

  setOperators: (operators) =>
    set((draft) => {
      draft.operators = operators;
    }),

  rehydrateForEdit: (payload) =>
    set((draft) => {
      const { config, parameters, originalParameters } = buildToolRehydrationState({
        payload,
        currentAdvancedParametersCollapsed: initialState.advancedParametersCollapsed,
      });
      Object.assign(draft, initialState, config, {
        formula: payload.formula ?? initialState.formula,
        parameters: parameters.map(({ name, item }) => ({ identifier: name, item })),
        originalParameters,
      });
    }),

  reset: resetState,
});

const STORE_NAME = 'FormulaTool';

const [FormulaToolStoreProvider, useFormulaToolStoreInstance, useFormulaToolStoreSelector] = createStore(
  STORE_NAME,
  initialState,
  formulaToolActionsFactory,
);

export { FormulaToolStoreProvider };

export const useFormulaToolId = () => useFormulaToolStoreSelector((state) => state.id);
export const useFormulaToolName = () => useFormulaToolStoreSelector((state) => state.name);
export const useFormulaToolFormula = () => useFormulaToolStoreSelector((state) => state.formula);
export const useFormulaToolParameters = () => useFormulaToolStoreSelector((state) => state.parameters);
export const useFormulaToolOriginalParameters = () => useFormulaToolStoreSelector((state) => state.originalParameters);
export const useFormulaToolNavigationStack = () => useFormulaToolStoreSelector((state) => state.navigationStack);
export const useFormulaToolFormulaFilter = () => useFormulaToolStoreSelector((state) => state.formulaFilter);
export const useFormulaToolOperators = () => useFormulaToolStoreSelector((state) => state.operators);
export const useFormulaToolActions = () => useFormulaToolStoreInstance().actions;

/**
 * Creates a new store instance for tests that must assert exact store state or when
 * porting Flux store tests that call actions imperatively.
 *
 * Prefer `<TestStoreProvider>` or `<*StoreProvider>` in tests and validate behavior
 * through UI interactions + DOM assertions whenever possible.
 *
 * NOTE: Only use this function in **test files**.
 *
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestFormulaToolStore(overrides?: Partial<FormulaToolState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestFormulaToolStore() should only be used in tests.');
  }
  return createStoreInstance({ ...initialState, ...overrides }, formulaToolActionsFactory);
}

/** @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. */
export function legacyCreateFormulaToolStore() {
  return createStoreInstance<FormulaToolState, FormulaToolActions>(initialState, formulaToolActionsFactory, {
    storeName: STORE_NAME,
  });
}

export const usePersistFormulaToolStore = () => {
  const register = useRegisterPersistedStore();
  const store = useFormulaToolStoreInstance();

  useEffect(
    () =>
      register({
        storeName: 'sqFormulaToolStore',
        persistenceLevel: 'WORKSHEET',
        store,
        dehydrate: dehydrateFormulaToolState,
      }),
    [register, store],
  );
};
